import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Trash2, UserCheck, X, Search, UserPlus, ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";
import {
  useCmGetActiveSetQuery,
  useCmGetModeratorsQuery,
  useCmCreateModeratorMutation,
  useCmDeleteModeratorMutation,
} from "../../api/chiefModeratorApi";
import { useLazySearchMembersByQQuery } from "../../../application/api/probationerApplicationApi";

function extractError(err: any): string {
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

type SelectedMember = { id: string; surname?: string; other_names?: string; email?: string; phone?: string; membership_no?: string; };

function fullName(m?: SelectedMember) {
  return [m?.surname, m?.other_names].filter(Boolean).join(" ") || "—";
}

export default function ManageModeratorsPage() {
  const { data: activeSet, isLoading: loadingSet } = useCmGetActiveSetQuery();
  const { data: moderators = [], isLoading } = useCmGetModeratorsQuery();
  const [createModerator, { isLoading: creating }] = useCmCreateModeratorMutation();
  const [deleteModerator, { isLoading: deleting }] = useCmDeleteModeratorMutation();

  const noActiveSet = !loadingSet && activeSet === null;

  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<SelectedMember | null>(null);
  const [addError, setAddError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [triggerSearch, searchState] = useLazySearchMembersByQQuery();
  const searchResults = useMemo(() => ((searchState.data as any)?.data || []) as SelectedMember[], [searchState.data]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!showSearch || q.trim().length < 2) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => triggerSearch(q.trim()), 350);
    return () => clearTimeout(debounceRef.current);
  }, [q, showSearch, triggerSearch]);

  const openSearch = () => {
    if (noActiveSet) return;
    setShowSearch(true); setSelected(null); setQ(""); setAddError("");
  };
  const closeSearch = () => { setShowSearch(false); setSelected(null); setQ(""); setAddError(""); };

  const handleAdd = async () => {
    if (!selected) return;
    setAddError("");
    try {
      await createModerator({ user_id: selected.id, name: fullName(selected), email: selected.email ?? "", phone: selected.phone } as any).unwrap();
      closeSearch();
      setMsg({ type: "success", text: `${fullName(selected)} added as moderator` });
    } catch (err) { setAddError(extractError(err)); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteModerator(id).unwrap();
      setDeleteTarget(null);
      setMsg({ type: "success", text: "Moderator removed" });
    } catch { setDeleteTarget(null); }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-indigo-50 text-indigo-600 shrink-0">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Total Moderators</div>
            <div className="text-xl font-black text-gray-900">{moderators.length}</div>
          </div>
        </div>

        <div className={`bg-white border p-5 rounded-3xl shadow-sm flex items-center gap-4 ${noActiveSet ? "border-amber-200 bg-amber-50/30" : "border-gray-100"}`}>
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${noActiveSet ? "bg-amber-100 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Executive Set</div>
            {loadingSet ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
            ) : noActiveSet ? (
              <div className="text-sm font-black text-amber-600">None active</div>
            ) : (
              <div className="text-sm font-black text-emerald-700">{activeSet?.name}</div>
            )}
          </div>
        </div>
      </div>

      {/* No active set warning */}
      {noActiveSet && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800 font-medium">
            Moderators cannot be added until an executive set is marked as active. Contact the admin to activate an executive set.
          </p>
        </div>
      )}

      {/* Flash message */}
      {msg && (
        <div className={`rounded-2xl px-6 py-3 text-sm font-bold flex items-center justify-between ${msg.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-700 border border-rose-100"}`}>
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-4 opacity-50 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <Link to="/exams/chief-moderator" className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </Link>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <UserCheck className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Manage Moderators</h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">{moderators.length} moderator{moderators.length !== 1 ? "s" : ""} assigned.</p>
            </div>
          </div>

          <button onClick={openSearch}
            disabled={noActiveSet}
            className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-6 py-3.5 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-indigo-600 transition-all shadow-lg shadow-gray-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed">
            <UserPlus className="w-4 h-4" /> Add Moderator
          </button>
        </div>
      </div>

      {/* Search panel */}
      {showSearch && (
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Search Members</p>
            <button onClick={closeSearch} className="p-1.5 hover:bg-gray-100 rounded-xl"><X className="w-3.5 h-3.5 text-gray-400" /></button>
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <input
              autoFocus
              value={q}
              onChange={(e) => { setQ(e.target.value); setSelected(null); }}
              placeholder="Search by name or membership number…"
              className="w-full pl-11 pr-4 py-3.5 border-b-2 border-gray-100 font-bold text-gray-800 text-sm outline-none focus:border-indigo-500 bg-transparent"
            />
            {searchState.isFetching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-300" />}
          </div>

          {!selected && searchResults.length > 0 && (
            <div className="border border-gray-100 rounded-2xl overflow-hidden divide-y divide-gray-50">
              {searchResults.map((m) => (
                <button key={m.id} onClick={() => setSelected(m)}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-indigo-50 transition-colors text-left">
                  <div>
                    <div className="font-bold text-sm text-gray-900">{fullName(m)}</div>
                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">{m.membership_no ?? m.email}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selected && (
            <div className="border border-indigo-100 bg-indigo-50 rounded-2xl p-5 space-y-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Selected Member</p>
              <div>
                <div className="font-black text-gray-900">{fullName(selected)}</div>
                <div className="text-[10px] text-gray-400 font-mono mt-0.5">{selected.membership_no ?? selected.email}</div>
              </div>
              {addError && <p className="text-[10px] font-black uppercase text-rose-500">{addError}</p>}
              <div className="flex gap-3">
                <button onClick={handleAdd} disabled={creating}
                  className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-indigo-600 transition-all disabled:opacity-30 active:scale-95">
                  {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                  {creating ? "Adding…" : "Add as Moderator"}
                </button>
                <button onClick={() => setSelected(null)}
                  className="py-3 px-5 text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
                  Change
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Moderator list */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Moderators {moderators.length > 0 ? `(${moderators.length})` : ""}
          </div>
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-200" /></div>
        ) : moderators.length === 0 ? (
          <div className="py-24 text-center">
            <UserCheck className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No moderators added yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {(moderators as any[]).map((m: any) => (
              <div key={m.id} className="flex items-center justify-between px-8 py-5 hover:bg-gray-50/80 transition-all gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-black text-gray-900 uppercase tracking-tight">{m.name}</div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                    {m.email}
                    {m.phone && <> &nbsp;·&nbsp; {m.phone}</>}
                  </div>
                  {m.executive_set_name && (
                    <span className="mt-2 inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-violet-50 text-violet-700 border border-violet-100">
                      {m.executive_set_name}
                    </span>
                  )}
                </div>

                {deleteTarget === String(m.id) ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleDelete(String(m.id))} disabled={deleting}
                      className="inline-flex items-center gap-1.5 bg-rose-600 text-white rounded-2xl px-4 py-2 text-[9px] font-black uppercase tracking-wider hover:bg-rose-700 transition-all disabled:opacity-30 active:scale-95">
                      {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      Confirm
                    </button>
                    <button onClick={() => setDeleteTarget(null)}
                      className="py-2 px-3 text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setDeleteTarget(String(m.id))}
                    className="p-2 rounded-xl text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-all shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
